import type { z } from 'zod';
import { stripDocumentSeparators } from '@/shared/format/internal';
import { billingSchema, billingPageSchema, pixSchema } from './billing.wire';
/* eslint-disable lingui/no-unlocalized-strings -- U02: wire identifiers and synthetic backend values. */
import { billingApi, requestBlob, ApiError } from '@/shared/http';
import type { BillingParams, BillingPatch, BillingPix } from './billing.wire';

function wire<S extends z.ZodType>(schema: S, data: unknown): z.output<S> {
  // Empty record responses are handled by the block empty state.
  if (data === null) return data as z.output<S>;
  const parsed = schema.safeParse(data);
  if (parsed.success) return parsed.data;
  if (!import.meta.env.PROD) console.warn('billing response shape mismatch');
  return data as z.output<S>;
} // U02: services/walletManagement/billing/index.ts:21-28; ParamsFilterBilling.ts:5-32.
export function normalizeBillingParams(params: BillingParams): BillingParams {
  const next = { ...params };
  if (typeof next['payerRegistrationNumber'] === 'string') {
    const raw = stripDocumentSeparators(next['payerRegistrationNumber']);
    next['payerRegistrationNumber'] =
      /^\d+$/.test(raw) && raw.startsWith('0') && raw.length < 11
        ? raw.padStart(11, '0')
        : raw;
  }
  if (typeof next['searchString'] === 'string') {
    const raw = next['searchString'].replaceAll('-', '').replaceAll('.', '');
    if (/^\d+$/.test(raw)) next['searchString'] = raw.startsWith('0') ? raw.slice(1) : raw;
  }
  return next;
}
export const listBillings = async (params: BillingParams, signal?: AbortSignal) =>
  wire(
    billingPageSchema,
    await billingApi.get<unknown>('/Billing', {
      params: normalizeBillingParams(params),
      timeout: 300_000,
      ...(signal ? { signal } : {}),
    }),
  ); // U02: billing/index.ts:39-44,122-131,146-155,158-163,214-228.
export const getBilling = async (id: string, signal?: AbortSignal) =>
  wire(billingSchema, await billingApi.get<unknown>(`/Billing/${id}`, signal ? { signal } : {}));
export const updateBilling = ({ id, body }: { id: string; body: BillingPatch }) =>
  billingApi.patch(`/Billing/${id}`, body);
export const cancelBilling = ({ id, isPaid }: { id: string; isPaid: boolean }) =>
  billingApi.post(`Billing/${id}/Cancel`, { isPaid });
export const sendBillingEmail = ({ id }: { id: string }) =>
  billingApi.post(`Billing/${id}/SendEmail`, {});
export const updateBillingCommunication = ({
  id,
  communicationSettingId,
}: {
  id: string;
  communicationSettingId: string;
}) =>
  billingApi.put(
    `/Billing/${id}/UpdateCommunicationSetting`,
    {},
    { params: { communicationSettingId } },
  );
// U02: billing/index.ts:186-211; billingContext.tsx:517-525.
export const exportBillings = (format: 'csv' | 'cnab', params: BillingParams) =>
  requestBlob(
    billingApi,
    {
      method: 'GET',
      url: format === 'csv' ? '/Billing/Export' : '/Billing/ExportAsCNAB',
      params: normalizeBillingParams(params),
      timeout: 300_000,
    },
    { fallbackFileName: format === 'csv' ? 'billings.csv' : 'billings.cnab' },
  );
// U02: billing/index.ts:53-73; billingContext.tsx:114-120.
export async function getBillingPix(id: string, signal?: AbortSignal): Promise<BillingPix | null> {
  try {
    return wire(
      pixSchema,
      await billingApi.get<unknown>(`/Billing/${id}/PixQrCode`, signal ? { signal } : {}),
    );
  } catch (error) {
    if (ApiError.from(error).status === 404) return null;
    throw error;
  }
}
export const downloadBillingPix = (id: string) =>
  requestBlob(
    billingApi,
    { method: 'GET', url: `/Billing/${id}/PixQrCode/PDF` },
    { fallbackFileName: 'pix.pdf' },
  );

// U02: billing/index.ts:231-271; billingContext get-comment appends tenant then page.
export const cancelBillingFees = (body: {
  ourNumber: number;
  ourNumberDigit: string;
  payerRegistrationNumber: string;
  amount: number;
  occurrenceDate: string;
  fees: number;
}) => billingApi.post('/DebtCollection/CancelWithdrawnFees', body);
export const getBillingComments = (id: string, params: BillingParams) =>
  billingApi.get<{
    data: {
      id: string;
      comment: string;
      createdAt?: string;
      createdBy?: { userIdDisplay?: string } | null;
    }[];
    page: number;
    totalPages: number;
  }>(`BillingComment/${id}`, { params });
export const postBillingComment = ({ id, comment }: { id: string; comment: string }) =>
  billingApi.post(`BillingComment/${id}`, { comment });
// U02: services/walletManagement/files/fileConfirmation/fileConfirmation.ts:51-66.
export const downloadBillingHistoryReceipt = (id: string) =>
  requestBlob(
    billingApi,
    {
      method: 'POST',
      data: {},
      url: `/FileConfirmation/Transaction/${id}/DownloadReceipt?IsBilling=true`,
    },
    { fallbackFileName: 'receipt.pdf' },
  );
// U02: billing/index.ts:31-37,76-84,92-105,122-131.
export const createBilling = async ({
  body,
  idempotence,
}: {
  body: Record<string, unknown>;
  idempotence?: string;
}) => {
  const created = wire(
    billingSchema,
    await billingApi.post('/Billing', body, { params: { idempotence } }),
  );
  if (typeof created.id !== 'string' || created.id.length === 0) {
    throw new Error('Billing creation response did not include an id');
  }
  return created;
};
export const patchBillingWorkflow = ({ id, body }: { id: string; body: Record<string, unknown> }) =>
  billingApi.patch(`/Billing/${id}`, body);
export const registerBilling = ({
  id,
  paymentMethods,
}: {
  id: string;
  paymentMethods?: readonly ('BankSlip' | 'Pix')[];
}) => billingApi.post(`/Billing/${id}/Register`, paymentMethods?.length ? { paymentMethods } : {});
export const getBillingTags = (
  { walletCode, searchString }: { walletCode: string | number; searchString?: string },
  signal?: AbortSignal,
) =>
  billingApi.get<string[]>('/Billing/Tags', {
    params: { searchString, walletCode },
    ...(signal ? { signal } : {}),
  });
